import { AccountInfoResponse, QuotaResponse } from 'types/billing';

type TrialState = AccountInfoResponse['trial'];
type PlanState = QuotaResponse['plan'];

/**
 * Single source of truth for whether trial UI (day-band hero, final-day
 * countdown, trial mascot) should be surfaced.
 *
 * Trial and paid state are INDEPENDENT, non-mutually-exclusive signals:
 *  - `trial.active` (/account-info) is a pure date check — `now < trial_end_date`
 *    — with no payment awareness.
 *  - the paid transition happens out-of-band (codium-proxy) and never clears
 *    `trial_end_date`, so a paying customer can still report `trial.active`.
 *
 * Gating trial UI on `trial.active` alone would therefore show a paying customer
 * "your trial ends in N days" and a ticking countdown. We require BOTH an active
 * trial AND the absence of a *paid* plan.
 *
 * Note `/quota` returns a `plan` during the trial too (`name: 'Trial'`,
 * `credits_granted: 0`), so "has a plan" is NOT the same as "is paying". The
 * trustworthy paid signal is granted credits: a purchased package grants a
 * positive amount, while the trial grants none. This also guards the
 * eventual-consistency window where stale trial state races a freshly
 * provisioned paid plan.
 *
 * @param trial Trial info from /account-info (PLT-4042), or null.
 * @param plan  Plan from /quota, or null. A trial-era plan grants 0 credits.
 */
export const isPaidPlan = (plan: QuotaResponse['plan']): boolean =>
  plan != null && plan.credits_granted > 0;

export const isTrialUiActive = (trial: TrialState, plan: PlanState): boolean =>
  Boolean(trial?.active) && !isPaidPlan(plan);
